import { useCallback, useEffect, useRef, useState } from "react";
import { assetEditorApi } from "../../api/products.api";
import { extractErrorMessage } from "../../utils/errorMessage";
import { useToast } from "../../hooks/useToast";
import type {
  AnchorBinding,
  ColorAdjust,
  EditorStateResponse,
  EditorZone,
  OrientationOverride,
  PlacementContract,
  ZoneOverride,
} from "../../types/product.types";

interface AssetEditorProps {
  productId: string;
  assetId: string;
  glbProxyUrl: string | null;
  /** Called after publish or reset so the parent can refresh the GLB proxy URL and apply orientation */
  onPublished?: (newGlbUrl: string | null, orientationOverride?: OrientationOverride | null) => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function hexToRgba(hex: string): [number, number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return [r, g, b, 1];
}

function rgbaToHex(rgba: [number, number, number, number]): string {
  const toHex = (v: number) =>
    Math.round(Math.max(0, Math.min(1, v)) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(rgba[0])}${toHex(rgba[1])}${toHex(rgba[2])}`;
}

const NEUTRAL_COLOR_ADJUST: ColorAdjust = {
  hue: 0,
  saturation: 1,
  brightness: 1,
};

function isNeutralColorAdjust(adj?: ColorAdjust): boolean {
  if (!adj) return true;
  return (
    (adj.hue ?? 0) === 0 &&
    (adj.saturation ?? 1) === 1 &&
    (adj.brightness ?? 1) === 1
  );
}


/**
 * Merge published + draft for saveDraft when the server remaps zone keys (split on/off).
 * Ensures material data kept only in `publishedOverrides` is not lost during remap; draft wins per field.
 */
function mergeZoneOverridesForServer(
  published: Record<string, ZoneOverride> | undefined,
  draft: Record<string, ZoneOverride>,
): Record<string, ZoneOverride> {
  const p = published ?? {};
  const keys = new Set([...Object.keys(p), ...Object.keys(draft)]);
  const out: Record<string, ZoneOverride> = {};
  for (const k of keys) {
    out[k] = { ...(p[k] ?? {}), ...(draft[k] ?? {}) };
  }
  return out;
}

// ── HSL color adjust helpers (mirrors backend logic) ─────────────────────────

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h, s, l];
}

function hue2rgb(p: number, q: number, t: number): number {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) return [l, l, l];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hue2rgb(p, q, h + 1 / 3), hue2rgb(p, q, h), hue2rgb(p, q, h - 1 / 3)];
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function faceSideGlyph(g?: EditorZone["faceSideGroup"]): string {
  if (!g) return "";
  const labels: Record<string, string> = {
    front: "▲ ",
    back: "▼ ",
    edges: "◆ ",
    pos_z: "F ",
    neg_z: "Bk ",
    pos_x: "R ",
    neg_x: "L ",
    pos_y: "U ",
    neg_y: "D ",
  };
  return labels[g] ?? "";
}

function applyColorAdjust(
  rgb: [number, number, number],
  adj: ColorAdjust,
): [number, number, number] {
  let [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  if (adj.hue) h = ((h + adj.hue / 360) % 1 + 1) % 1;
  if (adj.saturation !== undefined) s = clamp01(s * adj.saturation);
  if (adj.brightness !== undefined) l = clamp01(l * adj.brightness);
  const [r2, g2, b2] = hslToRgb(h, s, l);
  return [clamp01(r2), clamp01(g2), clamp01(b2)];
}

// ── Apply live material overrides to model-viewer element ─────────────────────
// model-viewer API: material.pbrMetallicRoughness.setBaseColorFactor / setRoughnessFactor / setMetallicFactor

function applyMaterialOverrides(
  mv: HTMLElement & { model?: { materials?: any[] } },
  overrides: Record<string, ZoneOverride>,
  zones: EditorZone[],
  _selectedZoneKey?: string | null,
) {
  const mats = mv.model?.materials;
  if (!mats || mats.length === 0) return;

  for (const zone of zones) {
    const override = overrides[zone.zoneKey];
    if (!override) continue;

    // Apply to all material indices for this zone (handles grouped duplicate-name zones)
    const indices = zone.sourceMaterialIndices ?? [zone.sourceMaterialIndex];
    for (const matIdx of indices) {
      const mat = mats[matIdx];
      if (!mat) continue;
      try {
        const pbr = mat.pbrMetallicRoughness;
        if (!pbr) continue;

        const ca = override.colorAdjust;
        const colorFromAdjust = !isNeutralColorAdjust(ca);
        const shouldSetBase =
          override.baseColorFactor !== undefined || colorFromAdjust;

        if (shouldSetBase) {
          let r0 = override.baseColorFactor?.[0];
          let g0 = override.baseColorFactor?.[1];
          let b0 = override.baseColorFactor?.[2];
          let a0 = override.baseColorFactor?.[3] ?? 1;
          if (r0 === undefined || g0 === undefined || b0 === undefined) {
            try {
              const cur = pbr.getBaseColorFactor?.() as
                | [number, number, number, number]
                | undefined;
              if (cur && cur.length >= 4) {
                [r0, g0, b0, a0] = cur;
              }
            } catch {
              /* ignore */
            }
          }
          const baseTuple: [number, number, number, number] = [
            r0 ?? 1,
            g0 ?? 1,
            b0 ?? 1,
            a0,
          ];
          let finalColor = baseTuple;
          if (colorFromAdjust && ca) {
            const [r, g, b] = applyColorAdjust(
              [finalColor[0], finalColor[1], finalColor[2]],
              ca,
            );
            finalColor = [r, g, b, finalColor[3]];
          }
          if (override.opacity !== undefined) {
            finalColor = [finalColor[0], finalColor[1], finalColor[2], override.opacity];
          }
          pbr.setBaseColorFactor(finalColor);
        } else if (override.opacity !== undefined) {
          try {
            const cur = pbr.getBaseColorFactor?.() as
              | [number, number, number, number]
              | undefined;
            if (cur && cur.length >= 4) {
              pbr.setBaseColorFactor([cur[0], cur[1], cur[2], override.opacity]);
            }
          } catch {
            /* ignore */
          }
        }

        // Live transparency preview — model-viewer exposes setAlphaMode on
        // the material (not on pbrMetallicRoughness).
        if (override.opacity !== undefined) {
          try {
            mat.setAlphaMode?.(override.opacity < 1 ? "BLEND" : "OPAQUE");
          } catch {
            /* ignore */
          }
        }

        if (override.roughnessFactor !== undefined) {
          pbr.setRoughnessFactor(override.roughnessFactor);
        }
        if (override.metalnessFactor !== undefined) {
          pbr.setMetallicFactor(override.metalnessFactor);
        }
      } catch {
        // model-viewer API may throw on certain GLBs — swallow silently
      }
    }
  }
}

// ── Anchor-transform (column-major mat4) decompose / recompose helpers ────────
// No `three` dependency in admin-frontend, so we do the mat4 math directly.
// Layout matches THREE.Matrix4 / WebGL: element [12..14] = translation column.

interface DecomposedTransform {
  translation: { x: number; y: number; z: number };
  /** Euler angles in degrees (XYZ order, applied intrinsically like model-viewer). */
  rotationDeg: { x: number; y: number; z: number };
  scale: number;
}

function decomposeAnchorTransform(m: number[]): DecomposedTransform {
  // Guard: fall back to identity-ish when the array is malformed.
  if (!Array.isArray(m) || m.length < 16) {
    return { translation: { x: 0, y: 0, z: 0 }, rotationDeg: { x: 0, y: 0, z: 0 }, scale: 1 };
  }
  const tx = m[12], ty = m[13], tz = m[14];
  // Column basis vectors (column-major): col0 = m[0..2], col1 = m[4..6], col2 = m[8..10].
  const sx = Math.hypot(m[0], m[1], m[2]);
  const sy = Math.hypot(m[4], m[5], m[6]);
  const sz = Math.hypot(m[8], m[9], m[10]);
  const scale = sx || 1; // uniform-scale contract; expose col0 length
  // Normalize rotation columns to strip scale before extracting Euler.
  const r00 = sx ? m[0] / sx : 0, r10 = sx ? m[1] / sx : 0, r20 = sx ? m[2] / sx : 0;
  const r01 = sy ? m[4] / sy : 0, r11 = sy ? m[5] / sy : 1, r21 = sy ? m[6] / sy : 0;
  const r02 = sz ? m[8] / sz : 0, r12 = sz ? m[9] / sz : 0, r22 = sz ? m[10] / sz : 1;
  // Extract intrinsic XYZ Euler (matches THREE.Euler 'XYZ' from a rotation matrix).
  let ex: number, ey: number, ez: number;
  ey = Math.asin(Math.max(-1, Math.min(1, r02)));
  if (Math.abs(r02) < 0.9999999) {
    ex = Math.atan2(-r12, r22);
    ez = Math.atan2(-r01, r00);
  } else {
    ex = Math.atan2(r21, r11);
    ez = 0;
  }
  const toDeg = (r: number) => (r * 180) / Math.PI;
  return {
    translation: { x: tx, y: ty, z: tz },
    rotationDeg: { x: toDeg(ex), y: toDeg(ey), z: toDeg(ez) },
    scale,
  };
}

function recomposeAnchorTransform(d: DecomposedTransform): number[] {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const cx = Math.cos(toRad(d.rotationDeg.x)), sx = Math.sin(toRad(d.rotationDeg.x));
  const cy = Math.cos(toRad(d.rotationDeg.y)), sy = Math.sin(toRad(d.rotationDeg.y));
  const cz = Math.cos(toRad(d.rotationDeg.z)), sz = Math.sin(toRad(d.rotationDeg.z));
  // Rotation matrix for intrinsic XYZ (R = Rx * Ry * Rz), row-major elements:
  const r00 = cy * cz;
  const r01 = -cy * sz;
  const r02 = sy;
  const r10 = cx * sz + sx * sy * cz;
  const r11 = cx * cz - sx * sy * sz;
  const r12 = -sx * cy;
  const r20 = sx * sz - cx * sy * cz;
  const r21 = sx * cz + cx * sy * sz;
  const r22 = cx * cy;
  const s = d.scale || 1;
  // Emit COLUMN-MAJOR: out[col*4 + row].
  return [
    r00 * s, r10 * s, r20 * s, 0,
    r01 * s, r11 * s, r21 * s, 0,
    r02 * s, r12 * s, r22 * s, 0,
    d.translation.x, d.translation.y, d.translation.z, 1,
  ];
}

function isProvisionalBinding(b: AnchorBinding): boolean {
  return b.reference_mesh_version === 0 || b.solve_method === "identity-fallback-v1";
}

// ── Main component ────────────────────────────────────────────────────────────

export function AssetEditor({ productId, assetId, glbProxyUrl, onPublished }: AssetEditorProps) {
  const { showSuccess, showError } = useToast();

  const [editorState, setEditorState] = useState<EditorStateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedZoneKey, setSelectedZoneKey] = useState<string | null>(null);
  const [localOverrides, setLocalOverrides] = useState<Record<string, ZoneOverride>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isClearingEditor, setIsClearingEditor] = useState(false);
  const [mvLoaded, setMvLoaded] = useState(false);
  // Local GLB blob URL — overrides the prop after a publish/reset so preview updates immediately
  const [localGlbBlobUrl, setLocalGlbBlobUrl] = useState<string | null>(null);
  const localGlbBlobRef = useRef<string | null>(null);
  // Split-preview GLB: when splitBySide is active we fetch a mesh-split version
  // from the backend so each side has its own material in model-viewer.
  const [splitPreviewBlobUrl, setSplitPreviewBlobUrl] = useState<string | null>(null);
  const splitPreviewBlobRef = useRef<string | null>(null);

  // Orientation override (rotation/translation via model-viewer; persisted in manifest, applied on storefront try-on)
  const [orientation, setOrientation] = useState<OrientationOverride>({});
  const [orientationOpen, setOrientationOpen] = useState(false);

  // Optional baked placement contract (anchor binding). Read from state when present,
  // sent back through the existing saveDraft manifest. Absent on legacy assets.
  const [placementContract, setPlacementContract] = useState<PlacementContract | null>(null);

  // ── Paint clipboard (copy/paste between surfaces) ────────────────────────────
  type PaintClip = { baseColorFactor?: [number,number,number,number]; roughnessFactor?: number; metalnessFactor?: number; opacity?: number; colorAdjust?: ColorAdjust; fromLabel?: string; };
  const [copiedPaint, setCopiedPaint] = useState<PaintClip | null>(null);

  const mvRef = useRef<HTMLElement & { model?: { materials?: any[] } }>(null);

  // ── Load editor state ───────────────────────────────────────────────────────

  // Refresh the local GLB blob URL after publish/reset so the preview updates immediately
  const refreshLocalGlb = useCallback(async (assetId: string) => {
    try {
      const newBlobUrl = await assetEditorApi.fetchGlbBlobUrl(productId, assetId);
      if (localGlbBlobRef.current) URL.revokeObjectURL(localGlbBlobRef.current);
      localGlbBlobRef.current = newBlobUrl;
      setLocalGlbBlobUrl(newBlobUrl);
      setMvLoaded(false);
    } catch {
      // fallback: parent will eventually re-fetch via onPublished
    }
  }, [productId]);

  const loadEditorState = useCallback(async () => {
    setLoading(true);
    try {
      const state = await assetEditorApi.getState(productId);
      setEditorState(state);
      setLocalOverrides(
        mergeZoneOverridesForServer(state.publishedOverrides, state.draftOverrides),
      );
      setIsDirty(false);
      const loadedOrientation = state.orientationOverride ?? {};
      setOrientation(loadedOrientation);
      setPlacementContract(state.placement_contract ?? null);
      // Always reset to first zone of the newly loaded product.
      // If we keep the old selectedZoneKey it might not exist in this product's
      // zones and MaterialControls would never render.
      setSelectedZoneKey(state.zones.length > 0 ? state.zones[0].zoneKey : null);
    } catch (err: any) {
      showError(extractErrorMessage(err, "Failed to load editor state."));
    } finally {
      setLoading(false);
    }
  }, [productId, showError]);

  useEffect(() => {
    loadEditorState();
    return () => {
      if (localGlbBlobRef.current) {
        URL.revokeObjectURL(localGlbBlobRef.current);
        localGlbBlobRef.current = null;
      }
    };
  }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch split-preview GLB when splitBySide is enabled ──────────────────────
  // The preview GLB has genuinely separate mesh primitives per side, so
  // model-viewer can apply different materials to Front / Back / Sides.
  useEffect(() => {
    const isSplit = editorState?.capabilities.isSplitBySide;
    if (!isSplit) {
      // Clean up any existing split blob URL
      if (splitPreviewBlobRef.current) {
        URL.revokeObjectURL(splitPreviewBlobRef.current);
        splitPreviewBlobRef.current = null;
      }
      setSplitPreviewBlobUrl(null);
      return;
    }
    let cancelled = false;
    assetEditorApi.getPreviewGlbBlobUrl(productId).then((url) => {
      if (cancelled) { URL.revokeObjectURL(url); return; }
      if (splitPreviewBlobRef.current) URL.revokeObjectURL(splitPreviewBlobRef.current);
      splitPreviewBlobRef.current = url;
      setSplitPreviewBlobUrl(url);
      setMvLoaded(false); // force model-viewer to reload with new GLB
    }).catch(() => { /* fall back to normal glbProxyUrl */ });
    return () => { cancelled = true; };
  }, [editorState?.capabilities.isSplitBySide, productId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Apply live material overrides when overrides change or model loads ───────
  // Note: we attempt to apply even when mvLoaded is false in case the model
  // was already loaded before the state update (e.g. cached GLB).

  useEffect(() => {
    const mv = mvRef.current;
    if (!mv || !editorState) return;
    applyMaterialOverrides(mv, localOverrides, editorState.zones);
  }, [localOverrides, mvLoaded, editorState]);

  // ── Called by EditorModelViewer after the model-viewer 'load' event ─────────

  const handleMvLoad = useCallback(() => {
    setMvLoaded(true);
    // Apply draft overrides immediately after model loads
    const mv = mvRef.current;
    if (mv && editorState) {
      applyMaterialOverrides(mv, localOverrides, editorState.zones);
    }
  }, [editorState, localOverrides]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived state ───────────────────────────────────────────────────────────

  const selectedZone: EditorZone | null =
    editorState?.zones.find((z) => z.zoneKey === selectedZoneKey) ?? null;

  const rawSelectedOverride = localOverrides[selectedZoneKey ?? ""] ?? {};
  const selectedOverride: ZoneOverride = {
    ...rawSelectedOverride,
    roughnessFactor: rawSelectedOverride.roughnessFactor ?? 0.5,
    metalnessFactor: rawSelectedOverride.metalnessFactor ?? 0.0,
    colorAdjust:
      rawSelectedOverride.colorAdjust ?? { ...NEUTRAL_COLOR_ADJUST },
  };

  const hasPairForSelected =
    !!selectedZone?.symmetryPairZoneKey && !!editorState?.capabilities.hasSymmetry;

  // ── Override mutation helpers ────────────────────────────────────────────────

  function setOverrideField<K extends keyof ZoneOverride>(field: K, value: ZoneOverride[K]) {
    if (!selectedZoneKey) return;
    setLocalOverrides((prev) => {
      const existing = prev[selectedZoneKey] ?? {};
      const updated = { ...existing, [field]: value };

      const result: Record<string, ZoneOverride> = { ...prev, [selectedZoneKey]: updated };
      if (updated.mirroredToPair && selectedZone?.symmetryPairZoneKey) {
        result[selectedZone.symmetryPairZoneKey] = {
          ...updated,
          mirroredToPair: false,
        };
      }
      return result;
    });
    setIsDirty(true);
  }

  function setColorAdjustField(field: keyof ColorAdjust, value: number) {
    const current = selectedOverride.colorAdjust ?? { hue: 0, saturation: 1, brightness: 1 };
    setOverrideField("colorAdjust", { ...current, [field]: value });
  }

  function revertZone() {
    if (!selectedZoneKey) return;
    setLocalOverrides((prev) => {
      const next = { ...prev };
      delete next[selectedZoneKey];
      return next;
    });
    setIsDirty(true);
  }

  // ── Paint clipboard ──────────────────────────────────────────────────────────

  function handleCopyPaint() {
    if (!selectedZoneKey || !selectedZone) return;
    const override = localOverrides[selectedZoneKey] ?? {};
    // Only copy fields that have actually been set — don't copy undefined so
    // paste doesn't wipe the target zone's untouched settings.
    const clip: PaintClip = { fromLabel: selectedZone.label };
    if (override.baseColorFactor !== undefined) clip.baseColorFactor = override.baseColorFactor;
    if (override.roughnessFactor !== undefined) clip.roughnessFactor = override.roughnessFactor;
    if (override.metalnessFactor !== undefined) clip.metalnessFactor = override.metalnessFactor;
    if (override.opacity !== undefined) clip.opacity = override.opacity;
    if (override.colorAdjust !== undefined) clip.colorAdjust = override.colorAdjust;

    const hasAnything = clip.baseColorFactor || clip.roughnessFactor !== undefined ||
      clip.metalnessFactor !== undefined || clip.opacity !== undefined || clip.colorAdjust;
    if (!hasAnything) {
      // For split zones: allow copying even without overrides — paste will be a no-op
      // but the clipboard state lets the user at least paste once they've edited something.
      if (!selectedZone.faceSideGroup) {
        showError("This surface has no paint settings yet. Edit it first, then copy.");
        return;
      }
      // Fall through with empty clip for split zones — paste acts as clear
    }
    setCopiedPaint(clip);
    showSuccess(`Paint copied from "${selectedZone.label}"`);
  }

  function handlePastePaint(zoneKey: string) {
    if (!copiedPaint) return;
    const { fromLabel: _, ...rawFields } = copiedPaint;
    // Strip undefined values so we don't accidentally clear fields on the target
    const paintFields = Object.fromEntries(
      Object.entries(rawFields).filter(([, v]) => v !== undefined),
    ) as Omit<PaintClip, "fromLabel">;
    setLocalOverrides((prev) => ({
      ...prev,
      [zoneKey]: { ...(prev[zoneKey] ?? {}), ...paintFields },
    }));
    setIsDirty(true);
  }

  function handlePasteToAll() {
    if (!copiedPaint || !editorState) return;
    const { fromLabel: _, ...rawFields } = copiedPaint;
    const paintFields = Object.fromEntries(
      Object.entries(rawFields).filter(([, v]) => v !== undefined),
    ) as Omit<PaintClip, "fromLabel">;
    setLocalOverrides((prev) => {
      const next = { ...prev };
      for (const zone of editorState.zones) {
        next[zone.zoneKey] = { ...(next[zone.zoneKey] ?? {}), ...paintFields };
      }
      return next;
    });
    setIsDirty(true);
    showSuccess("Paint applied to all surfaces");
  }

  // ── Actions ──────────────────────────────────────────────────────────────────

  /** Persist zones, material draft, split flag, and orientation to the server (single source of truth). */
  async function persistEditorManifest(silent: boolean): Promise<EditorStateResponse> {
    if (!editorState) {
      throw new Error("Editor state not loaded");
    }
    const updated = await assetEditorApi.saveDraft(productId, {
      manifest: {
        version: 1,
        zones: editorState.zones,
        draftOverrides: localOverrides,
        publishedOverrides: editorState.publishedOverrides,
        splitBySide: editorState.capabilities.isSplitBySide,
        orientationOverride: orientation,
        // Backend validates the contract defensively (malformed anchor_binding
        // is dropped server-side) and persists it in the editor manifest.
        ...(placementContract ? { placement_contract: placementContract } : {}),
      },
    });
    setEditorState(updated);
    setPlacementContract(updated.placement_contract ?? placementContract);
    setIsDirty(false);
    if (!silent) showSuccess("Draft saved.");
    return updated;
  }

  async function handleSaveDraft() {
    if (!editorState) return;
    setIsSavingDraft(true);
    try {
      await persistEditorManifest(false);
    } catch (err: any) {
      showError(extractErrorMessage(err, "Failed to save draft."));
    } finally {
      setIsSavingDraft(false);
    }
  }

  async function handleEnableSplit() {
    if (!editorState) return;
    setIsSavingDraft(true);
    try {
      const updated = await assetEditorApi.saveDraft(productId, {
        manifest: {
          version: 1,
          draftOverrides: mergeZoneOverridesForServer(
            editorState.publishedOverrides,
            localOverrides,
          ),
          publishedOverrides: editorState.publishedOverrides,
          splitBySide: true,
          orientationOverride: orientation,
        },
      });
      setEditorState(updated);
      setLocalOverrides(
        mergeZoneOverridesForServer(updated.publishedOverrides, updated.draftOverrides ?? {}),
      );
      setIsDirty(false);
      setSelectedZoneKey((prev) => {
        if (prev && updated.zones.some((z) => z.zoneKey === prev)) return prev;
        return updated.zones[0]?.zoneKey ?? null;
      });
      onPublished?.(updated.activeGlbUrl, updated.orientationOverride);
      showSuccess(
        "Mesh split into axis-aligned sides (up to six, depending on geometry).",
      );
    } catch (err: any) {
      showError(extractErrorMessage(err, "Failed to enable side split."));
    } finally {
      setIsSavingDraft(false);
    }
  }

  async function handleDisableSplit() {
    if (!editorState) return;
    setIsSavingDraft(true);
    try {
      const updated = await assetEditorApi.saveDraft(productId, {
        manifest: {
          version: 1,
          draftOverrides: mergeZoneOverridesForServer(
            editorState.publishedOverrides,
            localOverrides,
          ),
          publishedOverrides: editorState.publishedOverrides,
          splitBySide: false,
          orientationOverride: orientation,
        },
      });
      setEditorState(updated);
      setLocalOverrides(
        mergeZoneOverridesForServer(updated.publishedOverrides, updated.draftOverrides ?? {}),
      );
      setIsDirty(false);
      setSelectedZoneKey((prev) => {
        if (prev && updated.zones.some((z) => z.zoneKey === prev)) return prev;
        return updated.zones[0]?.zoneKey ?? null;
      });
      onPublished?.(updated.activeGlbUrl, updated.orientationOverride);
      showSuccess("Reverted to single surface.");
    } catch (err: any) {
      showError(extractErrorMessage(err, "Failed to revert split."));
    } finally {
      setIsSavingDraft(false);
    }
  }

  async function handlePublish() {
    if (!editorState) return;
    const hasMaterialChanges = isDirty || Object.keys(editorState.draftOverrides).length > 0;
    const hasOrientationSet = Object.values(editorState.orientationOverride ?? {}).some((v) => v !== undefined && v !== 0) || Object.values(orientation).some((v) => v !== undefined && v !== 0);
    if (!hasMaterialChanges && !hasOrientationSet) {
      showError("No changes to publish. Adjust at least one material or orientation setting.");
      return;
    }
    setIsPublishing(true);
    try {
      // Always sync manifest (including orientation) before publish. If the user clicked
      // "Draft saved" earlier, isDirty is false but React orientation must still hit the DB
      // or publish reads a stale manifest and the preview resets after the new GLB loads.
      await persistEditorManifest(true);
      const updated = await assetEditorApi.publish(productId);
      setEditorState(updated);
      // Clear local overrides to a clean slate — the new baked GLB already has the
      // material values embedded, so no model-viewer API overrides are needed.
      // Preserving publishedOverrides as localOverrides would double-apply colorAdjust
      // on the new baked geometry (the baked GLB uses original_color as its baseline,
      // so applyMaterialOverrides would re-adjust an already-adjusted color).
      setLocalOverrides({});
      setIsDirty(false);
      setOrientation(updated.orientationOverride ?? {});
      onPublished?.(updated.activeGlbUrl, updated.orientationOverride);
      await refreshLocalGlb(updated.assetId ?? assetId);
      showSuccess("Asset published! The storefront will now use the edited 3D model.");
    } catch (err: any) {
      showError(extractErrorMessage(err, "Failed to publish. Please try again."));
    } finally {
      setIsPublishing(false);
    }
  }

  /** Clears draft material + orientation only; does not change the GLB on disk. */
  async function handleResetEditorPanel() {
    if (!editorState) return;
    if (
      !confirm(
        "Clear all editor settings (material tweaks and orientation)? The 3D file on the storefront stays as-is until you publish new changes.",
      )
    ) {
      return;
    }
    setIsClearingEditor(true);
    try {
      const updated = await assetEditorApi.saveDraft(productId, {
        manifest: {
          version: 1,
          zones: editorState.zones,
          draftOverrides: {},
          publishedOverrides: editorState.publishedOverrides,
          splitBySide: editorState.capabilities.isSplitBySide,
          orientationOverride: {},
        },
      });
      setEditorState(updated);
      setLocalOverrides(
        mergeZoneOverridesForServer(updated.publishedOverrides, updated.draftOverrides ?? {}),
      );
      setOrientation({});
      setIsDirty(false);
      const el = mvRef.current as any;
      if (el) {
        try {
          el.orientation = "0deg 0deg 0deg";
        } catch {
          /* ignore */
        }
        try {
          el.cameraTarget = "0m 0m 0m";
        } catch {
          /* ignore */
        }
      }
      showSuccess("Editor cleared. Adjust settings and publish when ready.");
    } catch (err: any) {
      showError(extractErrorMessage(err, "Failed to clear editor."));
    } finally {
      setIsClearingEditor(false);
    }
  }

  /** Restores the pre-editor GLB URL and clears manifest (destructive). */
  async function handleRestoreOriginalFile() {
    if (!editorState?.hasPublishedEdits) {
      return;
    }
    if (
      !confirm(
        "Restore the original 3D file from before any editor publishes? This replaces the storefront model and cannot be undone from here.",
      )
    ) {
      return;
    }
    setIsResetting(true);
    try {
      const updated = await assetEditorApi.reset(productId);
      setEditorState(updated);
      setLocalOverrides({});
      setIsDirty(false);
      setOrientation({});
      const el = mvRef.current as any;
      if (el) {
        try {
          el.orientation = "0deg 0deg 0deg";
        } catch {
          /* ignore */
        }
        try {
          el.cameraTarget = "0m 0m 0m";
        } catch {
          /* ignore */
        }
      }
      onPublished?.(updated.activeGlbUrl, updated.orientationOverride);
      await refreshLocalGlb(updated.assetId ?? assetId);
      showSuccess("Original 3D file restored.");
    } catch (err: any) {
      showError(
        extractErrorMessage(err, "Failed to restore original file."),
      );
    } finally {
      setIsResetting(false);
    }
  }

  function handleRevertAll() {
    if (!editorState) return;
    setLocalOverrides(
      mergeZoneOverridesForServer(
        editorState.publishedOverrides,
        editorState.draftOverrides,
      ),
    );
    setOrientation(editorState.orientationOverride ?? {});
    setPlacementContract(editorState.placement_contract ?? null);
    setIsDirty(false);
  }

  // ── Anchor binding manual override ──────────────────────────────────────────
  // Rebuild the 16-float column-major anchor_transform from a decomposed edit,
  // stamp solve_method = 'editor-manual-v1', and mark dirty so the existing
  // Save/Publish flow persists it through the saveDraft manifest.
  function updateAnchorBinding(next: DecomposedTransform) {
    setPlacementContract((prev) => {
      const binding = prev?.anchor_binding;
      if (!binding) return prev ?? null;
      const updatedBinding: AnchorBinding = {
        ...binding,
        anchor_transform: recomposeAnchorTransform(next),
        solve_method: "editor-manual-v1",
      };
      return { ...(prev ?? {}), anchor_binding: updatedBinding };
    });
    setIsDirty(true);
  }

  // ── Rendering ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-slate-500">
        Loading editor…
      </div>
    );
  }

  if (!editorState?.capabilities.canEdit) {
    return (
      <div className="space-y-2 py-4 text-sm text-slate-500">
        <p>No editable asset available. Generate a 3D model first.</p>
      </div>
    );
  }

  const { zones, capabilities } = editorState;
  const hasOrientationSet = Object.values(orientation).some((v) => v !== undefined && v !== 0);
  const hasAnyPublished = editorState.hasPublishedEdits;

  function zoneRowLabel(zone: EditorZone): string {
    if (capabilities.isGlobalOnly) return "Whole model";
    if (zone.faceSideGroup) return faceSideGlyph(zone.faceSideGroup) + zone.label;
    return zone.label;
  }

  const selectedZoneForPicker =
    zones.find((z) => z.zoneKey === selectedZoneKey) ?? zones[0] ?? null;

  return (
    <div className="space-y-5">
      {/* Header / status row */}
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-base font-semibold text-slate-900">Material Editor</h3>
        {capabilities.isGlobalOnly && (
          <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
            Single surface
          </span>
        )}
        {capabilities.isSplitBySide && (
          <span className="rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-xs font-medium text-teal-700">
            Split: Front / Back / Right / Left / Up / Down
          </span>
        )}
        {hasAnyPublished && (
          <span className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
            Edits published (v{editorState.editorRevision})
          </span>
        )}
        {isDirty && (
          <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
            Unsaved changes
          </span>
        )}
        {editorState.draftUpdatedAt && !isDirty && (
          <span className="text-xs text-slate-400">
            Draft saved {new Date(editorState.draftUpdatedAt).toLocaleString()}
          </span>
        )}
        {editorState.reprocessing && (
          <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
            Reprocessing… publishing is temporarily disabled
          </span>
        )}
      </div>

      {editorState.reprocessWarning && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {editorState.reprocessWarning}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-5">
        {/* Left: Live 3D preview */}
        <div className="lg:col-span-3 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            Live preview — changes reflect instantly
          </p>
          <div className="rounded-2xl border border-slate-100 bg-white/60 overflow-hidden">
            {(splitPreviewBlobUrl || localGlbBlobUrl || glbProxyUrl) ? (
              <EditorModelViewer
                src={splitPreviewBlobUrl ?? localGlbBlobUrl ?? glbProxyUrl!}
                mvRef={mvRef}
                onLoad={handleMvLoad}
                orientation={orientation}
                showRulers={orientationOpen}
              />
            ) : (
              <div className="flex min-h-[320px] items-center justify-center text-sm text-slate-400">
                3D model loading…
              </div>
            )}
          </div>
          {!mvLoaded && (splitPreviewBlobUrl || localGlbBlobUrl || glbProxyUrl) && (
            <p className="text-xs text-slate-400 text-center">
              Loading 3D model for live preview…
            </p>
          )}
        </div>

        {/* Right: Zone list + material controls */}
        <div className="lg:col-span-2 space-y-4">
          {/* Zone selector */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
              {capabilities.isGlobalOnly
                ? "Surface (whole model)"
                : capabilities.isSplitBySide
                  ? `Sides (${zones.length})`
                  : `Surfaces (${zones.length})`}
            </p>

            {/* Clipboard banner */}
            {copiedPaint && (
              <div className="mb-2 flex items-center justify-between gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2">
                <p className="text-[11px] text-indigo-700 truncate">
                  Paint copied from <strong>"{copiedPaint.fromLabel}"</strong>
                </p>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={handlePasteToAll}
                    className="rounded-lg bg-indigo-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-indigo-700"
                  >
                    Paste to all
                  </button>
                  <button
                    type="button"
                    onClick={() => setCopiedPaint(null)}
                    className="text-indigo-400 hover:text-indigo-600 text-sm leading-none"
                    title="Clear clipboard"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}

            {zones.length > 0 && (
              <div className="flex items-stretch gap-2">
                <div className="relative min-w-0 flex-1">
                  <select
                    className="block w-full appearance-none rounded-xl border border-indigo-300 bg-indigo-50 py-2.5 pl-3 pr-10 text-sm font-medium text-indigo-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                    aria-label="Surface"
                    value={selectedZoneForPicker?.zoneKey ?? zones[0]!.zoneKey}
                    onChange={(e) => setSelectedZoneKey(e.target.value)}
                  >
                    {zones.map((zone) => {
                      const grp =
                        zone.sourceMaterialIndices && zone.sourceMaterialIndices.length > 1
                          ? ` ×${zone.sourceMaterialIndices.length}`
                          : "";
                      const sym = zone.symmetryGroup ? "⇄ " : "";
                      return (
                        <option key={zone.zoneKey} value={zone.zoneKey}>
                          {sym}
                          {zoneRowLabel(zone)}
                          {grp}
                        </option>
                      );
                    })}
                  </select>
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">
                    ▼
                  </span>
                </div>
                {copiedPaint && selectedZoneKey && (
                  <button
                    type="button"
                    onClick={() => handlePastePaint(selectedZoneKey)}
                    title={`Paste paint to the selected surface`}
                    className="shrink-0 self-center rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-[11px] font-semibold text-indigo-600 hover:bg-indigo-100"
                  >
                    Paste here
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Material controls — prompt if nothing selected */}
          {!selectedZone && zones.length > 0 && (
            <p className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-xs text-slate-400">
              Choose a surface from the menu to edit its paint settings.
            </p>
          )}

          {selectedZone && (
            <MaterialControls
              zone={selectedZone}
              override={selectedOverride}
              hasPair={hasPairForSelected}
              isGlobalOnly={capabilities.isGlobalOnly}
              copiedPaint={copiedPaint}
              onBaseColorChange={(hex) => setOverrideField("baseColorFactor", hexToRgba(hex))}
              onRoughnessChange={(v) => setOverrideField("roughnessFactor", v)}
              onMetalnessChange={(v) => setOverrideField("metalnessFactor", v)}
              onOpacityChange={(v) => setOverrideField("opacity", v)}
              onColorAdjustChange={setColorAdjustField}
              onMirrorToggle={(v) => setOverrideField("mirroredToPair", v)}
              onRevertZone={revertZone}
              onCopyPaint={handleCopyPaint}
              onPastePaint={() => handlePastePaint(selectedZone.zoneKey)}
            />
          )}
        </div>
      </div>

      {/* Orientation accordion — full width below the 2-col grid */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        <button
          type="button"
          onClick={() => setOrientationOpen((o) => !o)}
          className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 rounded-2xl"
        >
          <span className="flex items-center gap-2">
            <span className="text-slate-400">⊹</span>
            Orientation
            {(orientation.rx || orientation.ry || orientation.rz ||
              orientation.tx || orientation.ty || orientation.tz) && (
              <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-600">
                custom
              </span>
            )}
          </span>
          <span className="text-slate-400 text-xs">{orientationOpen ? "▲" : "▼"}</span>
        </button>
        {orientationOpen && (
          <div className="px-4 pb-4">
            <OrientationPanel
              value={orientation}
              onChange={(v) => { setOrientation(v); setIsDirty(true); }}
            />
          </div>
        )}
      </div>

      {/* Baked anchor binding — only rendered when the asset carries one. Additive:
          legacy assets (no anchor_binding) show a muted note instead. */}
      <AnchorBindingPanel
        binding={placementContract?.anchor_binding ?? null}
        onChange={updateAnchorBinding}
      />

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-4">
        <button
          type="button"
          onClick={handleSaveDraft}
          disabled={!isDirty || isSavingDraft || isPublishing || isClearingEditor}
          className="rounded-2xl border border-indigo-200 bg-indigo-50 px-5 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSavingDraft ? "Saving…" : "Save Draft"}
        </button>

        <button
          type="button"
          onClick={handlePublish}
          disabled={
            (!isDirty && Object.keys(editorState.draftOverrides).length === 0 &&
              !Object.values(editorState.orientationOverride ?? {}).some((v) => v !== undefined && v !== 0)) ||
            isPublishing ||
            isSavingDraft ||
            isClearingEditor ||
            !!editorState.reprocessing
          }
          className="rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 px-5 py-2 text-sm font-semibold text-white shadow-md shadow-indigo-200 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isPublishing ? "Publishing…" : "Publish to Storefront"}
        </button>

        {isDirty && (
          <button
            type="button"
            onClick={handleRevertAll}
            disabled={isSavingDraft || isPublishing || isClearingEditor}
            className="rounded-2xl border border-slate-200 bg-white px-5 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Revert Changes
          </button>
        )}

        <button
          type="button"
          onClick={handleResetEditorPanel}
          disabled={
            isClearingEditor ||
            isPublishing ||
            isSavingDraft ||
            isResetting
          }
          className="rounded-2xl border border-slate-200 bg-white px-5 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        >
          {isClearingEditor ? "Clearing…" : "Reset editor"}
        </button>

        {hasAnyPublished && (
          <button
            type="button"
            onClick={handleRestoreOriginalFile}
            disabled={
              isResetting ||
              isPublishing ||
              isSavingDraft ||
              isClearingEditor
            }
            className="rounded-2xl border border-rose-200 bg-white px-5 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-50"
          >
            {isResetting ? "Restoring…" : "Restore original file"}
          </button>
        )}
      </div>

      {/* Info note — offer axis split whenever the backend says the mesh can be split (including after undo split). */}
      {capabilities.canSplitBySide && !capabilities.isSplitBySide && (
        <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <span className="mt-0.5 text-slate-400 text-sm shrink-0">ℹ</span>
          <div className="min-w-0">
            <p className="text-xs text-slate-500 leading-relaxed mb-2">
              {capabilities.isGlobalOnly ? (
                <>
                  This model has a <strong>single unified surface</strong>. Adjust overall paint, or split into up to <strong>six axis-aligned sides</strong> to paint each facing direction independently.
                </>
              ) : (
                <>
                  This file has <strong>multiple material zones</strong> (for example after a published split). You can run <strong>axis split</strong> again using the original single-mesh GLB so each side has its own paint.
                </>
              )}
            </p>
            <button
              type="button"
              disabled={isSavingDraft}
              onClick={handleEnableSplit}
              className="rounded-lg border border-teal-300 bg-teal-50 px-3 py-1.5 text-xs font-semibold text-teal-700 hover:bg-teal-100 disabled:opacity-50"
            >
              ✦ Split into six axis-aligned sides
            </button>
          </div>
        </div>
      )}
      {capabilities.isSplitBySide && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-teal-200 bg-teal-50 px-4 py-3">
          <p className="text-xs text-teal-700">
            <strong>Multi-side split active</strong> — each facing direction has independent paint settings (up to six). Copy &amp; paste works between sides.
          </p>
          <button
            type="button"
            disabled={isSavingDraft}
            onClick={handleDisableSplit}
            className="shrink-0 text-xs text-teal-600 underline hover:text-teal-800 disabled:opacity-50"
          >
            Undo split
          </button>
        </div>
      )}
      <p className="text-xs text-slate-400">
        Adjust colour, roughness, metalness, and paint adjust (hue/saturation/brightness) — the 3D preview updates live as you edit.{" "}
        <strong>Save Draft</strong> preserves without republishing.{" "}
        <strong>Publish to Storefront</strong> bakes material edits into a new GLB and syncs your store; orientation is stored separately and applied during try-on so preview and storefront stay aligned.{" "}
        <strong>Reset editor</strong> clears panel settings only; <strong>Restore original file</strong> replaces the 3D file with the pre-editor version.
      </p>
    </div>
  );
}

// ── RulerOverlay ──────────────────────────────────────────────────────────────
// Draws minimal X/Y/Z axis rulers on a canvas overlay over the 3D preview.

function RulerOverlay() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    const cx = W / 2, cy = H / 2;
    const len = Math.min(W, H) * 0.38;
    const labels: Array<[string, number, number, number, number, string]> = [
      ["X", cx, cy, cx + len, cy, "#e05"],
      ["Y", cx, cy, cx, cy - len, "#0a0"],
      ["Z", cx, cy, cx - len * 0.55, cy + len * 0.55, "#07e"],
    ];

    for (const [label, x1, y1, x2, y2, color] of labels) {
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = 0.55;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      // Arrowhead
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const aw = 8;
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - aw * Math.cos(angle - 0.4), y2 - aw * Math.sin(angle - 0.4));
      ctx.lineTo(x2 - aw * Math.cos(angle + 0.4), y2 - aw * Math.sin(angle + 0.4));
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
      // Label
      ctx.globalAlpha = 0.8;
      ctx.fillStyle = color;
      ctx.font = "bold 11px sans-serif";
      ctx.fillText(label, x2 + 4, y2 + 4);
    }
    ctx.globalAlpha = 1;

    // Origin dot
    ctx.beginPath();
    ctx.arc(cx, cy, 3, 0, Math.PI * 2);
    ctx.fillStyle = "#fff";
    ctx.globalAlpha = 0.7;
    ctx.fill();
    ctx.globalAlpha = 1;
  }, []);

  return (
    <canvas
      ref={canvasRef}
      width={320}
      height={340}
      className="pointer-events-none absolute inset-0"
      style={{ width: "100%", height: "340px" }}
    />
  );
}

// ── Snap helpers ─────────────────────────────────────────────────────────────

const ROT_SNAPS = [0, 45, 90, 135, 180, -180, -135, -90, -45];
const POS_SNAP = 0;
const SNAP_ZONE_DEG = 3;      // degrees within which snap activates
const SNAP_ZONE_M = 0.015;    // metres within which snap activates for position

function snapRotation(val: number): { snapped: number; didSnap: boolean; snapTo: number | null } {
  for (const s of ROT_SNAPS) {
    if (Math.abs(val - s) < SNAP_ZONE_DEG) {
      return { snapped: s, didSnap: true, snapTo: s };
    }
  }
  return { snapped: val, didSnap: false, snapTo: null };
}

function snapPosition(val: number): { snapped: number; didSnap: boolean } {
  if (Math.abs(val - POS_SNAP) < SNAP_ZONE_M) {
    return { snapped: POS_SNAP, didSnap: true };
  }
  return { snapped: val, didSnap: false };
}

// ── OrientationPanel ─────────────────────────────────────────────────────────

interface OrientationPanelProps {
  value: OrientationOverride;
  onChange: (v: OrientationOverride) => void;
}

function OrientationPanel({ value, onChange }: OrientationPanelProps) {
  const [rotationOpen, setRotationOpen] = useState(false);
  const [positionOpen, setPositionOpen] = useState(false);
  // Flash state: which axis label and timeout ref
  const [snapFlash, setSnapFlash] = useState<string | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function triggerFlash(label: string) {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setSnapFlash(label);
    flashTimerRef.current = setTimeout(() => setSnapFlash(null), 2000);
  }

  function setRot(axis: "rx" | "ry" | "rz", raw: number) {
    const { snapped, didSnap, snapTo } = snapRotation(raw);
    if (didSnap && snapTo !== null) triggerFlash(`${axis.toUpperCase()} → ${snapTo}°`);
    onChange({ ...value, [axis]: snapped });
  }

  function setPos(axis: "tx" | "ty" | "tz", raw: number) {
    const { snapped, didSnap } = snapPosition(raw);
    if (didSnap) triggerFlash(`${axis.toUpperCase()} → 0`);
    onChange({ ...value, [axis]: snapped });
  }

  function autoOrient() {
    onChange({ rx: 0, ry: 0, rz: 0, tx: 0, ty: 0, tz: 0 });
    triggerFlash("Reset → 0,0,0");
  }

  const rotAxes: Array<{ key: "rx" | "ry" | "rz"; label: string; color: string }> = [
    { key: "rx", label: "Rotate X", color: "#e05555" },
    { key: "ry", label: "Rotate Y", color: "#22a85a" },
    { key: "rz", label: "Rotate Z", color: "#3b8ef0" },
  ];
  const posAxes: Array<{ key: "tx" | "ty" | "tz"; label: string; color: string }> = [
    { key: "tx", label: "Move X", color: "#e05555" },
    { key: "ty", label: "Move Y", color: "#22a85a" },
    { key: "tz", label: "Move Z", color: "#3b8ef0" },
  ];

  return (
    <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4">
      {/* Snap flash banner */}
      {snapFlash && (
        <div className="flex items-center gap-2 rounded-lg border border-rose-300 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600">
          <span className="block h-2 w-2 rounded-full bg-rose-500" />
          Snapped: {snapFlash}
        </div>
      )}

      {/* Auto-orient button */}
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Quick actions</p>
        <button
          type="button"
          onClick={autoOrient}
          className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700"
          title="Reset orientation to 0,0,0 and start fresh"
        >
          ⟳ Auto-orient
        </button>
      </div>

      {/* Rotation — nested accordion */}
      <div className="overflow-hidden rounded-xl border border-slate-100">
        <button
          type="button"
          onClick={() => setRotationOpen((o) => !o)}
          className="flex w-full items-center justify-between bg-slate-50/80 px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50"
        >
          <span>Rotation</span>
          <span className="text-slate-400">{rotationOpen ? "▲" : "▼"}</span>
        </button>
        {rotationOpen && (
          <div className="space-y-3 border-t border-slate-100 px-3 py-3">
            <p className="text-[10px] font-medium text-slate-400">Degrees (model-viewer order: X, Y, Z)</p>
            {rotAxes.map(({ key, label, color }) => {
              const val = value[key] ?? 0;
              return (
                <AxisSlider
                  key={key}
                  label={label}
                  color={color}
                  value={val}
                  min={-180}
                  max={180}
                  step={1}
                  unit="°"
                  snapPoints={ROT_SNAPS}
                  snapZone={SNAP_ZONE_DEG}
                  onChange={(v) => setRot(key, v)}
                  flashLabel={snapFlash}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Position — nested accordion */}
      <div className="overflow-hidden rounded-xl border border-slate-100">
        <button
          type="button"
          onClick={() => setPositionOpen((o) => !o)}
          className="flex w-full items-center justify-between bg-slate-50/80 px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50"
        >
          <span>Position</span>
          <span className="text-slate-400">{positionOpen ? "▲" : "▼"}</span>
        </button>
        {positionOpen && (
          <div className="space-y-3 border-t border-slate-100 px-3 py-3">
            <p className="text-[10px] font-medium text-slate-400">Metres (shift via camera target)</p>
            {posAxes.map(({ key, label, color }) => {
              const val = value[key] ?? 0;
              return (
                <AxisSlider
                  key={key}
                  label={label}
                  color={color}
                  value={val}
                  min={-1}
                  max={1}
                  step={0.005}
                  unit="m"
                  snapPoints={[0]}
                  snapZone={SNAP_ZONE_M}
                  onChange={(v) => setPos(key, v)}
                  flashLabel={snapFlash}
                />
              );
            })}
          </div>
        )}
      </div>

      <p className="text-[10px] text-slate-400 leading-snug">
        Snap lines appear at 0°, ±45°, ±90°, ±135°, ±180° for rotation and at 0 for position. Drag slowly past a snap point to override it.
      </p>
    </div>
  );
}

// ── AnchorBindingPanel ────────────────────────────────────────────────────────
// Displays a baked AnchorBinding (reference mesh id/version, solve method), the
// decomposed 16-float column-major anchor_transform, a PROVISIONAL badge when the
// binding is untrusted, and manual nudge controls that recompose the transform
// and stamp solve_method = 'editor-manual-v1'.

interface AnchorBindingPanelProps {
  binding: AnchorBinding | null;
  onChange: (next: DecomposedTransform) => void;
}

function AnchorBindingPanel({ binding, onChange }: AnchorBindingPanelProps) {
  const [open, setOpen] = useState(false);

  if (!binding) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
        <p className="text-xs text-slate-400 leading-relaxed">
          <span className="font-semibold text-slate-500">Anchor binding:</span>{" "}
          No baked anchor — the storefront will use its runtime placement heuristic for this asset.
        </p>
      </div>
    );
  }

  const decomposed = decomposeAnchorTransform(binding.anchor_transform);
  const provisional = isProvisionalBinding(binding);
  const t = decomposed.translation;
  const r = decomposed.rotationDeg;

  // Nudge helpers — build a fresh DecomposedTransform from the current binding.
  function nudgeTranslation(axis: "x" | "y" | "z", value: number) {
    onChange({ ...decomposed, translation: { ...t, [axis]: value } });
  }
  function nudgeRotation(axis: "x" | "y" | "z", value: number) {
    onChange({ ...decomposed, rotationDeg: { ...r, [axis]: value } });
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 rounded-2xl"
      >
        <span className="flex items-center gap-2">
          <span className="text-slate-400">⚓</span>
          Anchor binding
          {provisional ? (
            <span className="rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
              Provisional
            </span>
          ) : (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
              Baked
            </span>
          )}
          {binding.solve_method === "editor-manual-v1" && (
            <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-600">
              edited
            </span>
          )}
        </span>
        <span className="text-slate-400 text-xs">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="space-y-4 px-4 pb-4">
          {provisional && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <span className="mt-0.5 shrink-0">⚠</span>
              <span>
                This anchor is <strong>provisional / untrusted</strong>
                {binding.solve_method === "identity-fallback-v1"
                  ? " (identity fallback — no fit was solved)"
                  : binding.reference_mesh_version === 0
                    ? " (reference mesh version 0)"
                    : ""}
                . Verify placement in AR before relying on it; a manual nudge marks it editor-authored.
              </span>
            </div>
          )}

          {/* Metadata */}
          <div className="grid grid-cols-1 gap-1.5 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5 text-xs sm:grid-cols-3">
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Reference mesh</p>
              <p className="font-mono text-slate-700">{binding.reference_mesh_id}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Version</p>
              <p className="font-mono text-slate-700">{binding.reference_mesh_version}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Solve method</p>
              <p className="font-mono text-slate-700">{binding.solve_method}</p>
            </div>
          </div>

          {/* Decomposed transform + manual nudge */}
          <div className="space-y-3 rounded-xl border border-slate-100 px-3 py-3">
            <p className="text-[10px] font-medium text-slate-400">
              Anchor transform (decomposed from 16-float column-major matrix). Edit to nudge — saves as
              editor-manual-v1 via Save Draft / Publish.
            </p>

            <div>
              <p className="mb-1.5 text-[11px] font-semibold text-slate-500">Translation (m)</p>
              <div className="grid grid-cols-3 gap-2">
                <NumericField label="X" value={Number(t.x.toFixed(4))} min={-10} max={10} step={0.001} onChange={(v) => nudgeTranslation("x", v)} />
                <NumericField label="Y" value={Number(t.y.toFixed(4))} min={-10} max={10} step={0.001} onChange={(v) => nudgeTranslation("y", v)} />
                <NumericField label="Z" value={Number(t.z.toFixed(4))} min={-10} max={10} step={0.001} onChange={(v) => nudgeTranslation("z", v)} />
              </div>
            </div>

            <div>
              <p className="mb-1.5 text-[11px] font-semibold text-slate-500">Rotation (° — XYZ Euler)</p>
              <div className="grid grid-cols-3 gap-2">
                <NumericField label="X" value={Number(r.x.toFixed(2))} min={-180} max={180} step={0.5} onChange={(v) => nudgeRotation("x", v)} />
                <NumericField label="Y" value={Number(r.y.toFixed(2))} min={-180} max={180} step={0.5} onChange={(v) => nudgeRotation("y", v)} />
                <NumericField label="Z" value={Number(r.z.toFixed(2))} min={-180} max={180} step={0.5} onChange={(v) => nudgeRotation("z", v)} />
              </div>
            </div>

            {Math.abs(decomposed.scale - 1) > 1e-4 && (
              <p className="text-[10px] text-slate-400">
                Uniform scale: <span className="font-mono">{decomposed.scale.toFixed(4)}</span> (preserved; not editable here)
              </p>
            )}

            <details className="text-[10px] text-slate-400">
              <summary className="cursor-pointer select-none hover:text-slate-600">Raw matrix (column-major)</summary>
              <pre className="mt-1 overflow-x-auto rounded bg-slate-50 p-2 font-mono text-[10px] text-slate-500">
                {binding.anchor_transform.map((n) => Number(n.toFixed(4))).join(", ")}
              </pre>
            </details>
          </div>
        </div>
      )}
    </div>
  );
}

// ── AxisSlider ─────────────────────────────────────────────────────────────────

interface AxisSliderProps {
  label: string;
  color: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  snapPoints: number[];
  snapZone: number;
  onChange: (v: number) => void;
  flashLabel: string | null;
}

function AxisSlider({ label, color, value, min, max, step, unit, snapPoints, snapZone, onChange, flashLabel }: AxisSliderProps) {
  const isSnapped = snapPoints.some((s) => Math.abs(value - s) < 0.01);
  const snappedTo = isSnapped ? snapPoints.find((s) => Math.abs(value - s) < 0.01) : null;
  const showLine = flashLabel !== null && isSnapped;
  // Local state for the number input so the user can type freely without snap interrupting
  const [inputVal, setInputVal] = useState(String(Number(value.toFixed(3))));
  // Keep inputVal in sync when parent value changes (e.g. auto-orient reset)
  useEffect(() => {
    setInputVal(String(Number(value.toFixed(3))));
  }, [value]);

  const pct = ((value - min) / (max - min)) * 100;

  // Build tick marks at snap points
  const ticks = snapPoints.filter((s) => s >= min && s <= max).map((s) => ({
    pct: ((s - min) / (max - min)) * 100,
    label: s,
  }));

  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-600" style={{ color }}>{label}</span>
        <input
          type="number"
          value={inputVal}
          min={min}
          max={max}
          step={step}
          onChange={(e) => setInputVal(e.target.value)}
          onBlur={(e) => {
            const parsed = parseFloat(e.target.value);
            if (!isNaN(parsed)) onChange(Math.min(max, Math.max(min, parsed)));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const parsed = parseFloat((e.target as HTMLInputElement).value);
              if (!isNaN(parsed)) onChange(Math.min(max, Math.max(min, parsed)));
            }
          }}
          className="w-20 rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-right text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-300"
        />
      </div>
      <div className="relative">
        {/* Snap line flash */}
        {showLine && snappedTo !== null && (
          <div
            className="pointer-events-none absolute top-0 z-10 h-full w-0.5 bg-rose-500 rounded"
            style={{ left: `${pct}%`, transform: "translateX(-50%)" }}
          />
        )}
        {/* Tick marks */}
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 pointer-events-none">
          {ticks.map((t) => (
            <div
              key={t.label}
              className="absolute w-px h-2 bg-slate-300"
              style={{ left: `${t.pct}%`, transform: "translateX(-50%)" }}
            />
          ))}
        </div>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="relative z-0 w-full appearance-none accent-indigo-500 h-1.5 cursor-pointer"
          style={{ background: `linear-gradient(to right, ${color} ${pct}%, #e2e8f0 ${pct}%)` }}
        />
      </div>
      <div className="flex justify-between text-[9px] text-slate-300 px-0.5">
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
    </div>
  );
}

// ── EditorModelViewer ──────────────────────────────────────────────────────────
// Uses addEventListener('load') directly on the web component element because
// React synthetic event delegation does not reliably handle custom element events.

interface EditorModelViewerProps {
  src: string;
  mvRef: React.RefObject<HTMLElement & { model?: { materials?: any[] } }>;
  onLoad: () => void;
  orientation?: OrientationOverride;
  showRulers?: boolean;
}

function EditorModelViewer({
  src,
  mvRef,
  onLoad,
  orientation,
  showRulers,
}: EditorModelViewerProps) {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  // Keep onLoad stable via ref so the event listener doesn't need to re-register
  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;

  // Load the model-viewer script once
  useEffect(() => {
    if (customElements.get("model-viewer")) {
      setScriptLoaded(true);
      return;
    }
    const existing = document.querySelector(
      'script[src*="model-viewer"]',
    ) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => setScriptLoaded(true));
      // If already fully loaded (onload already fired)
      if ((existing as any).readyState === "complete" || existing.src) {
        setTimeout(() => {
          if (customElements.get("model-viewer")) setScriptLoaded(true);
        }, 100);
      }
      return;
    }
    const script = document.createElement("script");
    script.type = "module";
    script.src =
      "https://ajax.googleapis.com/ajax/libs/model-viewer/3.3.0/model-viewer.min.js";
    script.onload = () => setScriptLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Keep orientation in a ref for imperative model-viewer updates. Sync from props
  // in an effect only — assigning on every render races the publish flow: after we
  // set ref to {} for a baked reload, a re-render can overwrite it before delayed apply.
  const orientationRef = useRef(orientation);
  useEffect(() => {
    orientationRef.current = orientation;
  }, [orientation]);

  // Apply orientation + translation imperatively.
  // We NEVER set orientation via JSX attributes because model-viewer re-applies
  // the attribute on every React re-render, resetting the camera orbit each time.
  const applyOrientationImperative = useCallback((el: any, delay = false) => {
    if (!el) return;
    const doApply = () => {
      const o = orientationRef.current ?? {};
      const rx = o.rx ?? 0;
      const ry = o.ry ?? 0;
      const rz = o.rz ?? 0;
      const tx = o.tx ?? 0;
      const ty = o.ty ?? 0;
      const tz = o.tz ?? 0;
      // Set model rotation imperatively — avoids camera orbit reset
      try { el.orientation = `${rx}deg ${ry}deg ${rz}deg`; } catch { /* ignore */ }
      // Simulate translation by shifting camera-target
      try {
        el.cameraTarget = `${-tx}m ${-ty}m ${-tz}m`;
      } catch {
        el.setAttribute('camera-target', `${-tx}m ${-ty}m ${-tz}m`);
      }
    };
    if (delay) {
      // Double-rAF ensures model-viewer's own post-load updateFraming() has run first
      requestAnimationFrame(() => requestAnimationFrame(() => {
        doApply();
        // Apply a second time after 200ms in case model-viewer re-frames again
        setTimeout(doApply, 200);
      }));
    } else {
      doApply();
    }
  }, []);

  // Once model-viewer element is in the DOM, attach the 'load' event listener directly.
  // This is necessary because React's synthetic event system does not reliably fire
  // for custom events dispatched by web components.
  useEffect(() => {
    if (!scriptLoaded) return;
    const el = mvRef.current as any;
    if (!el) return;

    const handler = () => {
      // Use delay=true so we apply AFTER model-viewer's internal updateFraming()
      applyOrientationImperative(el, true);
      onLoadRef.current();
    };
    el.addEventListener("load", handler);

    // If model is already loaded (e.g. GLB was cached), fire immediately with delay
    if (el.model?.materials?.length > 0) {
      handler();
    }

    return () => el.removeEventListener("load", handler);
  }, [scriptLoaded, mvRef, applyOrientationImperative]);

  // When orientation sliders change (live editing), apply immediately without delay.
  useEffect(() => {
    if (!scriptLoaded) return;
    const el = mvRef.current as any;
    if (!el || !el.model?.materials) return; // only after model is loaded
    applyOrientationImperative(el, false);
  }, [scriptLoaded, mvRef, applyOrientationImperative,
      orientation?.rx, orientation?.ry, orientation?.rz,
      orientation?.tx, orientation?.ty, orientation?.tz]);

  // After publish/reset the preview `src` swaps to a new blob URL; re-apply orientation
  // once framing settles (load is not always reliable when only the blob URL changes).
  useEffect(() => {
    if (!scriptLoaded || !src) return;
    const el = mvRef.current as any;
    if (!el) return;
    const id = window.setTimeout(() => {
      if (el.model) applyOrientationImperative(el, true);
    }, 450);
    return () => window.clearTimeout(id);
  }, [src, scriptLoaded, applyOrientationImperative]);

  if (!scriptLoaded) {
    return (
      <div className="flex min-h-[320px] items-center justify-center text-sm text-slate-400">
        Loading viewer…
      </div>
    );
  }

  return (
    <div className="relative" style={{ width: "100%", height: "340px" }}>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore - model-viewer web component */}
      <model-viewer
        ref={mvRef}
        src={src}
        alt="3D model preview"
        camera-controls
        environment-image="neutral"
        shadow-intensity="0.6"
        shadow-softness="0.6"
        tone-mapping="neutral"
        style={{ width: "100%", height: "340px" }}
      />
      {showRulers && <RulerOverlay />}
    </div>
  );
}

// ── Tooltip component ─────────────────────────────────────────────────────────

interface TooltipProps {
  text: string;
}

function Tooltip({ text }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        className="ml-1 flex h-4 w-4 items-center justify-center rounded-full border border-slate-300 bg-slate-50 text-[10px] font-bold text-slate-500 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-600"
        aria-label="Help"
      >
        ?
      </button>
      {visible && (
        <span className="absolute bottom-full left-1/2 z-50 mb-1.5 w-56 -translate-x-1/2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[11px] leading-relaxed text-slate-600 shadow-lg">
          {text}
          <span className="absolute -bottom-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 border-b border-r border-slate-200 bg-white" />
        </span>
      )}
    </span>
  );
}

// ── MaterialControls ──────────────────────────────────────────────────────────

interface PaintClipType {
  baseColorFactor?: [number,number,number,number];
  roughnessFactor?: number;
  metalnessFactor?: number;
  opacity?: number;
  colorAdjust?: ColorAdjust;
  fromLabel?: string;
}

interface MaterialControlsProps {
  zone: EditorZone;
  override: ZoneOverride;
  hasPair: boolean;
  isGlobalOnly: boolean;
  copiedPaint: PaintClipType | null;
  onBaseColorChange: (hex: string) => void;
  onRoughnessChange: (v: number) => void;
  onMetalnessChange: (v: number) => void;
  onOpacityChange: (v: number) => void;
  onColorAdjustChange: (field: keyof ColorAdjust, value: number) => void;
  onMirrorToggle: (v: boolean) => void;
  onRevertZone: () => void;
  onCopyPaint: () => void;
  onPastePaint: () => void;
}

function MaterialControls({
  zone,
  override,
  hasPair,
  isGlobalOnly,
  copiedPaint,
  onBaseColorChange,
  onRoughnessChange,
  onMetalnessChange,
  onOpacityChange,
  onColorAdjustChange,
  onMirrorToggle,
  onRevertZone,
  onCopyPaint,
  onPastePaint,
}: MaterialControlsProps) {
  const colorHex = override.baseColorFactor
    ? rgbaToHex(override.baseColorFactor)
    : "#94a3b8";
  const roughness = override.roughnessFactor ?? 0.5;
  const metalness = override.metalnessFactor ?? 0.0;
  const opacity = override.opacity ?? 1.0;
  const ca = override.colorAdjust ?? { hue: 0, saturation: 1, brightness: 1 };

  return (
    <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-800 truncate">
          {isGlobalOnly
            ? "Whole model"
            : zone.faceSideGroup
              ? (zone.faceSideGroup === 'front' ? '▲ ' : zone.faceSideGroup === 'back' ? '▼ ' : '◆ ') + zone.label
              : zone.label}
        </p>
        <div className="flex items-center gap-1.5 shrink-0">
          {copiedPaint && (
            <button
              type="button"
              onClick={onPastePaint}
              title={`Paste paint from "${copiedPaint.fromLabel}"`}
              className="rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-100"
            >
              Paste paint
            </button>
          )}
          <button
            type="button"
            onClick={onCopyPaint}
            title="Copy this surface's paint settings"
            className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-100"
          >
            Copy paint
          </button>
          <button
            type="button"
            onClick={onRevertZone}
            className="text-xs text-slate-400 hover:text-rose-500"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Base color */}
      <label className="flex items-center justify-between gap-3">
        <span className="flex items-center text-xs text-slate-600">
          Base color
          <Tooltip text="The primary tint/color of this material. White means no tint (original texture color shows through). Pure black makes it completely dark." />
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400 font-mono">{colorHex}</span>
          <input
            type="color"
            value={colorHex}
            onChange={(e) => onBaseColorChange(e.target.value)}
            className="h-8 w-14 cursor-pointer rounded-lg border border-slate-200 p-0.5"
          />
        </div>
      </label>

      {/* Roughness */}
      <div className="space-y-1">
        <div className="flex justify-between text-xs text-slate-600">
          <span className="flex items-center">
            Roughness
            <Tooltip text="Controls how shiny or matte the surface looks. 0 = mirror-like gloss. 1 = fully matte/diffuse. Metals look best with low roughness." />
          </span>
          <span className="font-mono">{roughness.toFixed(2)}</span>
        </div>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={roughness}
          onChange={(e) => onRoughnessChange(parseFloat(e.target.value))}
          className="w-full accent-indigo-500"
        />
        <div className="flex justify-between text-[10px] text-slate-400">
          <span>Matte</span>
          <span>Glossy</span>
        </div>
      </div>

      {/* Metalness */}
      <div className="space-y-1">
        <div className="flex justify-between text-xs text-slate-600">
          <span className="flex items-center">
            Metalness
            <Tooltip text="How metallic the material appears. 0 = plastic/fabric/stone. 1 = pure metal (gold, silver, chrome). Use with low roughness for shiny metals." />
          </span>
          <span className="font-mono">{metalness.toFixed(2)}</span>
        </div>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={metalness}
          onChange={(e) => onMetalnessChange(parseFloat(e.target.value))}
          className="w-full accent-indigo-500"
        />
        <div className="flex justify-between text-[10px] text-slate-400">
          <span>Non-metal</span>
          <span>Metal</span>
        </div>
      </div>

      {/* Opacity */}
      <div className="space-y-1">
        <div className="flex justify-between text-xs text-slate-600">
          <span className="flex items-center">
            Opacity
            <Tooltip text="How see-through this surface is. 1 = fully solid. Lower values make it translucent (glass, visors, gems). The published model keeps this transparency in AR." />
          </span>
          <span className="font-mono">{opacity.toFixed(2)}</span>
        </div>
        <input
          type="range"
          min={0.05}
          max={1}
          step={0.01}
          value={opacity}
          onChange={(e) => onOpacityChange(parseFloat(e.target.value))}
          className="w-full accent-indigo-500"
        />
        <div className="flex justify-between text-[10px] text-slate-400">
          <span>Transparent</span>
          <span>Solid</span>
        </div>
      </div>

      {/* Paint Adjust (collapsed section) */}
      <PaintAdjustControls ca={ca} onChange={onColorAdjustChange} />

      {/* Symmetry mirror toggle */}
      {hasPair && zone.symmetryPairZoneKey && (
        <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={!!override.mirroredToPair}
            onChange={(e) => onMirrorToggle(e.target.checked)}
            className="accent-indigo-500"
          />
          Mirror to &quot;{zone.symmetryPairZoneKey.replace(/_/g, " ")}&quot;
        </label>
      )}
    </div>
  );
}

// ── TextureTransformControls ──────────────────────────────────────────────────

// ── PaintAdjustControls ───────────────────────────────────────────────────────

interface PaintAdjustControlsProps {
  ca: ColorAdjust;
  onChange: (field: keyof ColorAdjust, value: number) => void;
}

function PaintAdjustControls({ ca, onChange }: PaintAdjustControlsProps) {
  const [open, setOpen] = useState(false);
  const hue = ca.hue ?? 0;
  const saturation = ca.saturation ?? 1;
  const brightness = ca.brightness ?? 1;

  return (
    <div className="border-t border-slate-100 pt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-xs font-medium text-slate-500 hover:text-slate-700"
      >
        <span className="flex items-center gap-1">
          Paint Adjust
          <Tooltip text="Fine-tune the paint/colour of this material by shifting its hue, saturation, and brightness on top of the base colour you chose above. The preview updates live." />
        </span>
        <span>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-3 rounded-xl bg-slate-50 p-3">
          <p className="text-[10px] text-slate-400 leading-relaxed">
            These controls adjust the hue, saturation, and brightness of the paint colour — applied on top of the base colour above. The 3D preview updates live.
          </p>

          {/* Hue */}
          <div className="space-y-1">
            <div className="flex justify-between text-[11px] font-semibold text-slate-500">
              <span className="flex items-center">
                Hue Shift
                <Tooltip text="Rotates the colour wheel. 0 = original colour. +120 shifts green→blue. -120 shifts blue→green. Full range ±180°." />
              </span>
              <span className="font-mono text-slate-400">{hue > 0 ? "+" : ""}{Math.round(hue)}°</span>
            </div>
            <input
              type="range"
              min={-180}
              max={180}
              step={1}
              value={hue}
              onChange={(e) => onChange("hue", parseFloat(e.target.value))}
              className="w-full accent-indigo-500"
            />
            <div className="flex justify-between text-[10px] text-slate-400">
              <span>-180°</span>
              <span>0</span>
              <span>+180°</span>
            </div>
          </div>

          {/* Saturation */}
          <div className="space-y-1">
            <div className="flex justify-between text-[11px] font-semibold text-slate-500">
              <span className="flex items-center">
                Saturation
                <Tooltip text="How vivid or muted the colour is. 1 = unchanged. 0 = greyscale. 2 = double the vividness." />
              </span>
              <span className="font-mono text-slate-400">{saturation.toFixed(2)}×</span>
            </div>
            <input
              type="range"
              min={0}
              max={2}
              step={0.01}
              value={saturation}
              onChange={(e) => onChange("saturation", parseFloat(e.target.value))}
              className="w-full accent-indigo-500"
            />
            <div className="flex justify-between text-[10px] text-slate-400">
              <span>Greyscale</span>
              <span>Normal</span>
              <span>Vivid</span>
            </div>
          </div>

          {/* Brightness */}
          <div className="space-y-1">
            <div className="flex justify-between text-[11px] font-semibold text-slate-500">
              <span className="flex items-center">
                Brightness
                <Tooltip text="How light or dark the material appears. 1 = unchanged. 0 = fully black. 2 = double brightness (may wash out)." />
              </span>
              <span className="font-mono text-slate-400">{brightness.toFixed(2)}×</span>
            </div>
            <input
              type="range"
              min={0}
              max={2}
              step={0.01}
              value={brightness}
              onChange={(e) => onChange("brightness", parseFloat(e.target.value))}
              className="w-full accent-indigo-500"
            />
            <div className="flex justify-between text-[10px] text-slate-400">
              <span>Dark</span>
              <span>Normal</span>
              <span>Bright</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── NumericField ──────────────────────────────────────────────────────────────

interface NumericFieldProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}

function NumericField({ label, value, min, max, step, onChange }: NumericFieldProps) {
  return (
    <div className="space-y-0.5">
      <label className="text-[10px] text-slate-500">{label}</label>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          if (Number.isFinite(v)) onChange(v);
        }}
        className="w-full rounded-lg border border-slate-200 px-2 py-1 text-xs focus:border-indigo-300 focus:outline-none"
      />
    </div>
  );
}
